import React, { ButtonHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

// Определяем варианты стилей кнопки
const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap rounded-full font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                primary: 'bg-primary text-white hover:bg-primary-600 active:bg-primary-700',
                secondary: 'bg-apple-50 text-apple-800 hover:bg-apple-100 active:bg-apple-200',
                outline: 'border border-apple-200 bg-white hover:bg-apple-50 text-apple-800',
                ghost: 'hover:bg-apple-50 text-apple-800',
                destructive: 'bg-red-500 text-white hover:bg-red-600',
                link: 'text-primary underline-offset-4 hover:underline',
            },
            size: {
                sm: 'h-9 px-3 text-sm',
                md: 'h-10 px-5 text-base',
                lg: 'h-12 px-6 text-base',
                xl: 'h-14 px-8 text-lg',
            },
            fullWidth: {
                true: 'w-full',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'md',
            fullWidth: false,
        },
    }
);

// Определяем свойства для кнопки
export interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    href?: string;
    to?: string;
    isExternal?: boolean;
    isLoading?: boolean;
}

// Компонент кнопки
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            fullWidth,
            href,
            to,
            isExternal = false,
            isLoading = false,
            disabled,
            children,
            ...props
        },
        ref
    ) => {
        // Для отрисовки индикатора загрузки
        const loadingIndicator = (
            <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                ></circle>
                <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
            </svg>
        );

        // Конечные стили кнопки
        const classNames = cn(buttonVariants({ variant, size, fullWidth, className }));

        // Если кнопка - внешняя ссылка
        if (href) {
            return (
                <a
                    href={href}
                    className={classNames}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                >
                    {isLoading && loadingIndicator}
                    {children}
                </a>
            );
        }

        // Если кнопка - внутренняя ссылка с react-router
        if (to) {
            return (
                <Link to={to} className={classNames}>
                    {isLoading && loadingIndicator}
                    {children}
                </Link>
            );
        }

        // Обычная кнопка
        return (
            <button
                className={classNames}
                ref={ref}
                disabled={disabled || isLoading}
                {...props}
            >
                {isLoading && loadingIndicator}
                {children}
            </button>
        );
    }
);

Button.displayName = 'Button';

export { Button, buttonVariants }; 